<template>
  <div
    class="qty-stepper"
    :class="{ 'qty-stepper--saving': saving }"
  >
    <ekitabu-button
      class="qty-stepper__btn"
      :class="{ 'qty-stepper__btn--remove': removeAtOne && isMinimumQuantity }"
      text
      :width="30"
      :height="30"
      :min-width="0"
      :ripple="false"
      :aria-label="decrementAriaLabel"
      :disabled="decrementDisabled"
      @click="decrement"
    >
      <v-icon
        v-if="removeAtOne && isMinimumQuantity"
        size="14"
      >
        mdi-trash-can-outline
      </v-icon>
      <minus-icon v-else />
    </ekitabu-button>
    <input
      ref="qtyInput"
      v-model.lazy="displayQuantity"
      class="qty-stepper__value"
      :aria-label="$t('checkout.quantity')"
      type="text"
      inputmode="numeric"
      maxlength="3"
      @focus="$refs.qtyInput.select()"
      @blur="commitInput"
      @keydown.enter="$refs.qtyInput.blur()"
      @keydown.up.prevent="increment"
      @keydown.down.prevent="decrement"
    >
    <ekitabu-button
      class="qty-stepper__btn qty-stepper__btn--plus"
      text
      :width="30"
      :height="30"
      :min-width="0"
      :ripple="false"
      :aria-label="$t('checkout.increaseQuantity')"
      :disabled="currentQuantity >= MAX_QUANTITY || saving"
      @click="increment"
    >
      <plus-icon />
    </ekitabu-button>
    <v-progress-circular
      v-if="saving"
      class="qty-stepper__spinner"
      indeterminate
      size="14"
      width="2"
      color="primary"
    />
  </div>
</template>

<script>
import OrderQueries from "@/graphql/OrderQueries";
import MinusIcon from "@/components/icons/MinusIcon.vue";
import PlusIcon from "@/components/icons/PlusIcon.vue";

const MAX_QUANTITY = 999;
const DEBOUNCE_MS = 500;
const MAX_RETRIES = 2;

export default {
  name: "QuantityInput",
  components: {
    MinusIcon,
    PlusIcon,
  },
  props: {
    orderLineId: {
      type: String,
      required: true,
    },
    stepCount: {
      type: Number,
      default: 1,
    },
    quantity: {
      type: Number,
      required: true,
    },
    small: {
      type: Boolean,
      default: false,
    },
    removeAtOne: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      MAX_QUANTITY,
      currentQuantity: this.quantity,
      displayQuantity: String(this.quantity),
      saving: false,
      debounceTimer: null,
      pendingRetry: false,
      retryCount: 0,
    };
  },
  computed: {
    isMinimumQuantity() {
      return this.currentQuantity <= 1;
    },
    decrementDisabled() {
      return this.saving || (this.isMinimumQuantity && !this.removeAtOne);
    },
    decrementAriaLabel() {
      return this.removeAtOne && this.isMinimumQuantity
        ? this.$t('checkout.remove')
        : this.$t('checkout.decreaseQuantity');
    },
  },
  watch: {
    quantity(val) {
      // Don't override local state while user is editing or save is in-flight
      if (!this.debounceTimer && !this.saving) {
        this.currentQuantity = val;
        this.displayQuantity = String(val);
      }
    },
  },
  beforeDestroy() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  },
  methods: {
    increment() {
      if (this.currentQuantity >= MAX_QUANTITY) return;
      this.setQuantity(this.currentQuantity + this.stepCount);
    },
    decrement() {
      if (this.isMinimumQuantity) {
        if (this.removeAtOne) return this.removeLine();
        return;
      }
      this.setQuantity(this.currentQuantity - this.stepCount);
    },
    commitInput() {
      const raw = this.$refs.qtyInput ? this.$refs.qtyInput.value : this.displayQuantity;
      const parsed = parseInt(raw, 10);
      this.setQuantity(Number.isNaN(parsed) ? 1 : parsed);
    },
    setQuantity(val) {
      const clamped = Math.max(1, Math.min(MAX_QUANTITY, val));
      this.currentQuantity = clamped;
      this.displayQuantity = String(clamped);
      this.$emit("input", clamped);
      this.debouncedUpdate();
    },
    debouncedUpdate() {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.debounceTimer = null;
        if (this.saving) {
          this.pendingRetry = true;
          return;
        }
        this.updateQuantity();
      }, DEBOUNCE_MS);
    },
    async removeLine() {
      if (this.saving) return;
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
        this.debounceTimer = null;
      }

      this.saving = true;
      try {
        await this.$apollo.mutate({
          mutation: OrderQueries.deleteOrderLine,
          variables: { id: this.orderLineId },
        });
        this.retryCount = 0;
        this.pendingRetry = false;
        this.$emit("quantityUpdated");
        this.$emit("removed");
      } catch (error) {
        this.currentQuantity = this.quantity;
        this.displayQuantity = String(this.quantity);
        this.$emit("quantityFailed");
      } finally {
        this.saving = false;
      }
    },
    async updateQuantity() {
      this.saving = true;
      try {
        await this.$apollo.mutate({
          mutation: OrderQueries.adjustQuantity,
          variables: {
            input: {
              id: this.orderLineId,
              newQuantity: this.currentQuantity,
            },
          },
        });
        this.retryCount = 0;
        this.$emit("quantityUpdated");
      } catch (error) {
        this.currentQuantity = this.quantity;
        this.displayQuantity = String(this.quantity);
        this.$emit("quantityFailed");
      } finally {
        this.saving = false;
        if (this.pendingRetry) {
          this.pendingRetry = false;
          if (this.retryCount < MAX_RETRIES) {
            this.retryCount++;
            this.updateQuantity();
            return;
          }
        }
        // Reconcile if the prop changed while saving (watcher skips during save)
        if (!this.debounceTimer && this.quantity !== this.currentQuantity) {
          this.currentQuantity = this.quantity;
          this.displayQuantity = String(this.quantity);
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.qty-stepper {
  display: inline-flex;
  align-items: center;
  border: 1px solid $book-grey-lighter;
  border-radius: 6px;
  background: white;
  height: 30px;
  overflow: hidden;
  position: relative;
}

.qty-stepper__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: none;
  background: none;
  color: $book-grey;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  padding: 0;
  flex-shrink: 0;
}

.qty-stepper__btn:hover:not(:disabled) {
  background: $book-grey-lightest;
  color: $book-ink;
}

.qty-stepper__btn--plus:hover:not(:disabled) {
  background: rgba($book-red, 0.06);
  color: $book-red;
}

.qty-stepper__btn--remove:hover:not(:disabled) {
  background: rgba($book-red-danger, 0.06);
  color: $book-red-danger;
}

.qty-stepper__btn:disabled {
  color: $book-grey-light;
  cursor: not-allowed;
}

.qty-stepper__value {
  width: 34px;
  height: 30px;
  text-align: center;
  border: none;
  border-left: 1px solid $book-grey-lighter;
  border-right: 1px solid $book-grey-lighter;
  font-family: $book-font-body;
  font-size: 0.8125rem;
  font-weight: 700;
  color: $book-ink;
  background: transparent;
  outline: none;
  -moz-appearance: textfield;
  padding: 0;
  transition: opacity 0.15s;
}

.qty-stepper__value::-webkit-outer-spin-button,
.qty-stepper__value::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.qty-stepper__value:focus {
  background: rgba($book-red, 0.03);
}

// Saving state: the spinner sits centered inside the control (over the
// dimmed value) so the pill footprint never changes while a save is
// in flight.
.qty-stepper__spinner {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.qty-stepper--saving .qty-stepper__value {
  opacity: 0.35;
}
</style>
